import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import {
  portalSlice,
  useGetPortalMeQuery,
  useGetPortalSigningViewQuery,
  usePortalDeclineMutation,
  usePortalSignMutation,
} from "../../../slices/PortalSlice";
import { apiUrl, fetchPdf } from "../../../slices/SignaturesSlice";
import {
  SigningNotice,
  SigningView,
} from "../../signatures/components/SigningView";
import { FirmBadge, portalLink } from "../components/PortalUi";
import { errorMessage, isSignedOutError } from "../portal.utils";
import { getPortalSession, setPortalSession } from "../portalSession";

/** The client signs a document (portal sign-in or email code required). */
export const PortalSign = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const session = getPortalSession();
  const view = useGetPortalSigningViewQuery(id, { skip: !session || !id });
  const me = useGetPortalMeQuery(undefined, { skip: !session });
  const [sign] = usePortalSignMutation();
  const [decline] = usePortalDeclineMutation();

  const signOut = () => {
    setPortalSession(null);
    dispatch(portalSlice.util.resetApiState());
    navigate("/portal", {
      replace: true,
      state: { notice: "Please sign in again." },
    });
  };

  useEffect(() => {
    if (
      isSignedOutError(view.error) &&
      (view.error as { data?: unknown }).data === undefined
    )
      signOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.error]);

  if (!session) return <Navigate to={"/portal"} replace={true} />;

  const back = (
    <Link to={"/portal/home"} className={`${portalLink} mt-2 text-sm`}>
      Back to my portal
    </Link>
  );

  const myName =
    [me.data?.firstName, me.data?.lastName].filter(Boolean).join(" ") ||
    view.data?.signers.find((s) => s.isMe)?.name ||
    "";

  return (
    <div
      className={
        "flex min-h-screen flex-col bg-[#F1F2F5] font-[Inter,sans-serif] text-[#1F2433]"
      }>
      <header
        className={
          "flex items-center gap-3 border-b border-[#E8E8EC] bg-white px-4 py-3"
        }>
        <FirmBadge name={view.data?.firmName || "ProCharted"} size={"sm"} />
        <span className={"flex-1 text-sm font-extrabold"}>
          {view.data?.firmName || "Client portal"}
        </span>
        <Link to={"/portal/home"} className={`${portalLink} text-[13px]`}>
          My portal
        </Link>
      </header>
      {view.isLoading ? (
        <div
          className={"py-20 text-center text-sm font-semibold text-[#6B7185]"}>
          Opening the document…
        </div>
      ) : !view.data ? (
        <div>
          <SigningNotice
            title={"This document can’t be opened"}
            text={errorMessage(view.error, "Please try again in a moment.")}>
            {isSignedOutError(view.error) && (
              <button
                type={"button"}
                className={`${portalLink} text-sm`}
                onClick={signOut}>
                Sign in with another email
              </button>
            )}
            {back}
          </SigningNotice>
        </div>
      ) : (
        <SigningView
          view={view.data}
          identityNote={"Identity confirmed by email code"}
          defaultName={myName}
          loadPdf={() =>
            fetchPdf(
              apiUrl(`api/portal/signatures/${id}/document`),
              `Bearer ${getPortalSession()?.token ?? ""}`,
            )
          }
          onSign={async (body) => {
            const result = await sign({ id, body });
            if ("error" in result) return errorMessage(result.error);
            view.refetch();
            return null;
          }}
          onDecline={async (reason) => {
            const result = await decline({ id, reason });
            return "error" in result ? errorMessage(result.error) : null;
          }}
          doneActions={back}
        />
      )}
    </div>
  );
};
